import * as React from "react"
import { useState, useEffect, useCallback } from "react"
import { Drawer, Box, Typography, TextField, InputAdornment, Autocomplete, Avatar, Chip, Tooltip, IconButton, Button, Divider, CircularProgress, Checkbox } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { postSubmit, getSubmit } from "@ui-seeker/utils"
import { useAppToast } from "@ui-seeker/app"

import { BACKEND_APIS } from "@constants"
import { tableData, compareSite } from "../@signals"


export default function MapDrawer({ open, onClose, product }) {
    const { showToast } = useAppToast()
    const [sites, setSites] = useState([])
    const [selectedSite, setSelectedSite] = useState(null)
    const [searchText, setSearchText] = useState('')
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [offset, setOffset] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    const [selected, setSelected] = useState(new Set())
    const [mapping, setMapping] = useState(false)
    const [showMatched, setShowMatched] = useState(false)
    const [sameBrand, setSameBrand] = useState(true)
    const LIMIT = 20

    // Load sites (excluding source product's site). Pre-select compare site if set.
    useEffect(() => {
        if (open && product?.id_site && product?.id_customer) {
            getSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/all-sites?id_customer=${product.id_customer}`)
                .then(res => {
                    if (res) {
                        const filtered = res.filter(s => s.value !== product.id_site)
                        setSites(filtered)
                        if (compareSite.value) {
                            const preselected = filtered.find(s => s.value === compareSite.value.value)
                            if (preselected) setSelectedSite(preselected)
                        }
                    }
                })
                .catch(() => {})
        }
    }, [open, product?.id_site, product?.id_customer])

    // Reset state on close
    useEffect(() => {
        if (!open) {
            setSelectedSite(null)
            setSearchText('')
            setResults([])
            setOffset(0)
            setHasMore(true)
            setSelected(new Set())
            setMapping(false)
            setShowMatched(false)
            setSameBrand(true)
        }
    }, [open])

    const fetchProducts = useCallback((siteId, text, currentOffset, append = false) => {
        setLoading(true)

        postSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/search-raw`, {
            ...(siteId ? { id_site: siteId } : {}),
            id_customer: product?.id_customer,
            exclude_id_site: product?.id_site,
            search: text || null,
            offset: currentOffset,
            limit: LIMIT,
            ...(sameBrand ? {
                same_brand: true,
                origin_id_site: product?.id_site,
                origin_product_id: product?.product_id
            } : {})
        })
            .then(res => {
                if (res) {
                    if (append) {
                        setResults(prev => [...prev, ...res])
                    } else {
                        setResults(res)
                    }
                    setHasMore(res.length === LIMIT)
                }
            })
            .catch(() => showToast.error('Error searching products'))
            .finally(() => setLoading(false))
    }, [product?.id_customer, product?.id_site, product?.product_id, sameBrand])

    // Re-fetch when site changes
    useEffect(() => {
        if (selectedSite) {
            setOffset(0)
            setHasMore(true)
            setSelected(new Set())
            fetchProducts(selectedSite.value, searchText, 0)
        }
    }, [selectedSite])

    // Re-fetch when the "same brand" filter toggles (only if a search is already in play)
    useEffect(() => {
        if (results.length > 0 || selectedSite) {
            setOffset(0)
            setHasMore(true)
            setSelected(new Set())
            fetchProducts(selectedSite?.value, searchText, 0)
        }
    }, [sameBrand])

    const handleSearch = () => {
        setOffset(0)
        setHasMore(true)
        setSelected(new Set())
        fetchProducts(selectedSite?.value, searchText, 0)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch()
    }

    const handleLoadMore = () => {
        const newOffset = offset + LIMIT
        setOffset(newOffset)
        fetchProducts(selectedSite?.value, searchText, newOffset, true)
    }

    const toggleSelect = (item) => {
        const key = `${item.id_site}-${item.product_id}`
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const handleMapSelected = async () => {
        const targets = results.filter(r => selected.has(`${r.id_site}-${r.product_id}`))
        if (!targets.length) return

        setMapping(true)

        const promises = targets.map(target =>
            postSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/map`, {
                id_customer: product.id_customer,
                id_site: product.id_site,
                product_id: product.product_id,
                product_name: product.name,
                target_id_site: target.id_site,
                target_product_id: target.product_id
            }).then(res => ({ target, res }))
              .catch(() => ({ target, res: { success: false } }))
        )

        const outcomes = await Promise.all(promises)
        const successes = outcomes.filter(r => r.res?.success)
        const failures = outcomes.filter(r => !r.res?.success)

        if (successes.length > 0) {
            const newMatches = successes.map(({ target }) => ({
                id_site: target.id_site,
                product_id: target.product_id,
                name: target.name,
                image_url: target.image_url,
                product_url: target.product_url,
                variant_raw: target.variant_raw,
                site_name: target.site_name
            }))
            tableData.value = tableData.value.map(item =>
                item.id_site === product.id_site && item.product_id === product.product_id
                    ? { ...item, matches: [...(item.matches || []), ...newMatches] }
                    : item
            )
            showToast.success(`${successes.length} product${successes.length > 1 ? 's' : ''} matched`)
        }
        if (failures.length > 0) {
            showToast.error(`${failures.length} product${failures.length > 1 ? 's' : ''} failed`)
        }

        setMapping(false)
        if (failures.length === 0) {
            onClose()
        } else {
            // Keep drawer open, remove successful items from selection
            setSelected(prev => {
                const next = new Set(prev)
                successes.forEach(({ target }) => next.delete(`${target.id_site}-${target.product_id}`))
                return next
            })
        }
    }

    if (!product) return null

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 520 } }}
        >
            <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header: source product */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>
                        Add match for:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
                        <Avatar src={product.image_url} variant="rounded" sx={{ width: 50, height: 50, '& img': { objectFit: 'contain' } }}>
                            <IconSeeker icon="image" sx={{ color: '#9ca3af' }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.name}</Typography>
                            {product.variant_raw && (
                                <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca', px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                                    {product.variant_raw}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                                {product.product_id}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Site selector (optional) */}
                <Autocomplete
                    options={sites}
                    getOptionLabel={(opt) => opt.label || ''}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    value={selectedSite}
                    onChange={(_, val) => setSelectedSite(val)}
                    renderInput={(params) => <TextField {...params} label="Filter by site (optional)" size="small" />}
                    size="small"
                    sx={{ mb: 2 }}
                />

                {/* Search box - always visible */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, ID, EAN, brand..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSeeker icon="search" sx={{ fontSize: 16, color: '#9ca3af' }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleSearch}>
                                    <IconSeeker icon="arrow-right" sx={{ fontSize: 14 }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                {/* Filters: same brand + show matched */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={sameBrand}
                            onChange={(e) => setSameBrand(e.target.checked)}
                            size="small"
                            sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: '#10b981' } }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280', cursor: 'pointer' }} onClick={() => setSameBrand(v => !v)}>
                            Solo misma marca
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={showMatched}
                            onChange={(e) => setShowMatched(e.target.checked)}
                            size="small"
                            sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: '#6366f1' } }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280', cursor: 'pointer' }} onClick={() => setShowMatched(v => !v)}>
                            Show already matched
                        </Typography>
                    </Box>
                </Box>

                {/* Results list */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {results.filter(item => showMatched || !item.is_matched).map((item, i) => {
                        const key = `${item.id_site}-${item.product_id}`
                        const isSelected = selected.has(key)
                        return (
                            <Box
                                key={`${key}-${i}`}
                                sx={{
                                    display: 'flex', gap: 1, alignItems: 'center',
                                    p: 1.5, mb: 1, borderRadius: 1,
                                    border: isSelected ? '1px solid #10b981' : '1px solid #e5e7eb',
                                    bgcolor: isSelected ? '#f0fdf4' : 'transparent',
                                    '&:hover': { bgcolor: isSelected ? '#f0fdf4' : '#f9fafb', borderColor: '#10b981' },
                                    cursor: 'pointer'
                                }}
                                onClick={() => toggleSelect(item)}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: '#10b981' } }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleSelect(item)}
                                />
                                <Avatar src={item.image_url} variant="rounded" sx={{ width: 45, height: 45, flexShrink: 0, '& img': { objectFit: 'contain' } }}>
                                    <IconSeeker icon="image" sx={{ color: '#d1d5db', fontSize: 16 }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                            {item.name || item.product_id}
                                        </Typography>
                                        {item.product_url && (
                                            <a href={item.product_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'flex' }}>
                                                <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 10, color: '#6366f1', flexShrink: 0, mt: 0.25 }} />
                                            </a>
                                        )}
                                    </Box>
                                    {item.variant_raw && (
                                        <Typography component="span" variant="caption" sx={{ display: 'inline', fontSize: '0.65rem', fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca', px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                                            {item.variant_raw}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {item.site_name && <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 500, fontSize: '0.65rem' }}>{item.site_name}</Typography>}
                                        {item.brand && <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 500, fontSize: '0.65rem' }}>{item.brand}</Typography>}
                                        <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.6rem' }}>{item.product_id}</Typography>
                                        {item.ean && <Chip label={`EAN: ${item.ean}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />}
                                    </Box>
                                </Box>
                            </Box>
                        )
                    })}

                    {/* Loading */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {/* Load more */}
                    {!loading && hasMore && results.length > 0 && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Button variant="outlined" size="small" onClick={handleLoadMore}>
                                Load more
                            </Button>
                        </Box>
                    )}

                    {/* No results */}
                    {!loading && results.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                            <IconSeeker icon="search" sx={{ fontSize: 24, mb: 1 }} />
                            <Typography variant="body2">Search to find products</Typography>
                        </Box>
                    )}
                </Box>

                {/* Map selected button */}
                {selected.size > 0 && (
                    <Box sx={{ pt: 2, borderTop: '1px solid #e5e7eb' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleMapSelected}
                            disabled={mapping}
                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                            startIcon={mapping ? <CircularProgress size={16} color="inherit" /> : <IconSeeker icon="link" sx={{ fontSize: 16 }} />}
                        >
                            {mapping ? 'Mapping...' : `Map selected (${selected.size})`}
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    )
}
